import React from 'react';
import { InfoBadge } from '@medely/ui-kit';
import { ICondition } from '@medely/types';
import {
  useProfessionalQualifications,
  useCurrentUser,
  useNearFutureExpiringConditions,
} from '@mp';
import {
  getConditionProfessionalQualifications,
  getMissingConditionStatus,
  MissingConditionStatus,
} from '@mp/utils/missingConditionStatus';
import moment from 'moment';

interface MissingConditionListItemBadgeProps {
  condition: ICondition;
}

export const MissingConditionListItemBadge = ({
  condition,
  ...props
}: MissingConditionListItemBadgeProps) => {
  const { currentUser } = useCurrentUser();
  const { allProfessionalQualifications } = useProfessionalQualifications();
  const { expiringConditions } = useNearFutureExpiringConditions();
  const status = getMissingConditionStatus({
    condition,
    professionalQualifications: allProfessionalQualifications,
    satisfiedConditionIds: currentUser?.professional?.satisfied_condition_ids ?? [],
    expiringConditionIds: expiringConditions.map((condition) => condition.id),
  });

  if (status === MissingConditionStatus.InReview) {
    return <InfoBadge label="In review" color="info" {...props} />;
  }

  if (status === MissingConditionStatus.ExpiringSoon) {
    const professionalQualifications = getConditionProfessionalQualifications(
      condition,
      allProfessionalQualifications,
    );
    const date = professionalQualifications[0]?.professional_qualification_attributes?.find(
      (attribute) => attribute.qualification_attribute?.field_type === 'expiration_date',
    )?.value;
    const expirationDate = date ? moment(date).format('M/D/YY') : 'soon';
    return <InfoBadge label={`Expires ${expirationDate}`} color="warning" {...props} />;
  }

  if (status === MissingConditionStatus.NeedsAttention) {
    return <InfoBadge label="Needs attention" color="error" {...props} />;
  }

  if (status === MissingConditionStatus.Expired) {
    return <InfoBadge label="Expired" color="error" {...props} />;
  }

  if (status === MissingConditionStatus.Completed) {
    return <InfoBadge label="Completed" color="success" {...props} />;
  }

  return <InfoBadge label="Missing" color="error" {...props} />;
};
